/**
 * AcheteurModal — Modale profil acheteur (lecture seule, 1 onglet Présentation)
 * Reprend l'UI de ModalProfilMembre avec un seul onglet.
 */
import React from 'react';
import '../../pages/Publications/components/DetailCandidature/ModalProfilMembre.css';
import PerimetreMapDisplay from '../PerimetreSelector/PerimetreMapDisplay';
import type { PerimetreValue } from '../PerimetreSelector/PerimetreSelector';
import SectionPliable from '../SectionPliable/SectionPliable';

interface AcheteurData {
  id: number;
  nom: string;
  siret?: string | null;
  type_organisme?: string | null;
  presentation?: string | null;
  adresse?: string | null;
  code_postal?: string | null;
  departement_code?: string | null;
  pays?: string | null;
  ville?: string | null;
  has_recent_orders?: boolean;
  logo_url?: string | null;
  representant_1_prenom?: string | null;
  representant_1_nom?: string | null;
  representant_1_fonction?: string | null;
  representant_2_prenom?: string | null;
  representant_2_nom?: string | null;
  representant_2_fonction?: string | null;
  representant_3_prenom?: string | null;
  representant_3_nom?: string | null;
  representant_3_fonction?: string | null;
  perimetre_detail?: PerimetreValue | null;
}

interface AcheteurModalProps {
  isOpen: boolean;
  onClose: () => void;
  acheteur: AcheteurData | null;
  isLoading?: boolean;
}

const COUNTRY_FLAGS: Record<string, string> = {
  'France': '🇫🇷',
  'Belgique': '🇧🇪',
  'Luxembourg': '🇱🇺',
  'Suisse': '🇨🇭',
  'Allemagne': '🇩🇪',
  'Espagne': '🇪🇸',
  'Italie': '🇮🇹',
};

const DEPT_NAMES: Record<string, string> = {
  '01': 'Ain', '02': 'Aisne', '03': 'Allier', '04': 'Alpes-de-Haute-Provence',
  '05': 'Hautes-Alpes', '06': 'Alpes-Maritimes', '07': 'Ardèche', '08': 'Ardennes',
  '09': 'Ariège', '10': 'Aube', '11': 'Aude', '12': 'Aveyron',
  '13': 'Bouches-du-Rhône', '14': 'Calvados', '15': 'Cantal', '16': 'Charente',
  '17': 'Charente-Maritime', '18': 'Cher', '19': 'Corrèze',
  '2A': 'Corse-du-Sud', '2B': 'Haute-Corse',
  '21': 'Côte-d\'Or', '22': 'Côtes-d\'Armor', '23': 'Creuse', '24': 'Dordogne',
  '25': 'Doubs', '26': 'Drôme', '27': 'Eure', '28': 'Eure-et-Loir',
  '29': 'Finistère', '30': 'Gard', '31': 'Haute-Garonne', '32': 'Gers',
  '33': 'Gironde', '34': 'Hérault', '35': 'Ille-et-Vilaine', '36': 'Indre',
  '37': 'Indre-et-Loire', '38': 'Isère', '39': 'Jura', '40': 'Landes',
  '41': 'Loir-et-Cher', '42': 'Loire', '43': 'Haute-Loire', '44': 'Loire-Atlantique',
  '45': 'Loiret', '46': 'Lot', '47': 'Lot-et-Garonne', '48': 'Lozère',
  '49': 'Maine-et-Loire', '50': 'Manche', '51': 'Marne', '52': 'Haute-Marne',
  '53': 'Mayenne', '54': 'Meurthe-et-Moselle', '55': 'Meuse', '56': 'Morbihan',
  '57': 'Moselle', '58': 'Nièvre', '59': 'Nord', '60': 'Oise', '61': 'Orne',
  '62': 'Pas-de-Calais', '63': 'Puy-de-Dôme', '64': 'Pyrénées-Atlantiques',
  '65': 'Hautes-Pyrénées', '66': 'Pyrénées-Orientales', '67': 'Bas-Rhin',
  '68': 'Haut-Rhin', '69': 'Rhône', '70': 'Haute-Saône', '71': 'Saône-et-Loire',
  '72': 'Sarthe', '73': 'Savoie', '74': 'Haute-Savoie', '75': 'Paris',
  '76': 'Seine-Maritime', '77': 'Seine-et-Marne', '78': 'Yvelines',
  '79': 'Deux-Sèvres', '80': 'Somme', '81': 'Tarn', '82': 'Tarn-et-Garonne',
  '83': 'Var', '84': 'Vaucluse', '85': 'Vendée', '86': 'Vienne',
  '87': 'Haute-Vienne', '88': 'Vosges', '89': 'Yonne', '90': 'Territoire de Belfort',
  '91': 'Essonne', '92': 'Hauts-de-Seine', '93': 'Seine-Saint-Denis',
  '94': 'Val-de-Marne', '95': 'Val-d\'Oise',
  '971': 'Guadeloupe', '972': 'Martinique', '973': 'Guyane',
  '974': 'La Réunion', '976': 'Mayotte',
};

function formatDepartement(code: string | null | undefined, pays: string | null | undefined): string {
  if (!code) return pays || '';
  const name = DEPT_NAMES[code.toUpperCase()] || DEPT_NAMES[code] || code;
  const flag = pays ? (COUNTRY_FLAGS[pays] || '') : '';
  const paysStr = pays ? `, ${flag} ${pays}` : '';
  return `${code} - ${name}${paysStr}`;
}

const AcheteurModal: React.FC<AcheteurModalProps> = ({ isOpen, onClose, acheteur, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop modal-profil-backdrop" onClick={onClose}>
      <div className="modal-profil-membre" onClick={(e) => e.stopPropagation()}>
        <button className="modal-profil-close" onClick={onClose} aria-label="Fermer">✕</button>

        {/* Loading */}
        {isLoading && (
          <div className="modal-profil-loading">
            <div className="spinner" />
            <p>Chargement du profil…</p>
          </div>
        )}

        {/* Erreur */}
        {!isLoading && !acheteur && (
          <div className="modal-profil-error">
            <p>Organisme introuvable.</p>
          </div>
        )}

        {/* Contenu */}
        {acheteur && !isLoading && (
          <>
            {/* En-tête */}
            <div className="modal-profil-header">
              <div className="modal-profil-header-left">
                {acheteur.logo_url ? (
                  <img
                    src={acheteur.logo_url}
                    alt={`Logo ${acheteur.nom}`}
                    className="modal-profil-logo"
                  />
                ) : (
                  <div className="modal-profil-logo-placeholder">
                    LOGO
                  </div>
                )}
                <div className="modal-profil-identity">
                  <h2>{acheteur.nom}</h2>
                  <div className="modal-profil-details">
                    {acheteur.departement_code && (
                      <span>{formatDepartement(acheteur.departement_code, acheteur.pays)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Corps */}
            <div className="modal-profil-body">
              <div className="readonly-tab readonly-presentation">

                {/* Section Présentation — même structure que Profil.tsx */}
                <SectionPliable title="Présentation" className="readonly-section-pliable">
                  <div className="readonly-section-content">
                    {/* Nom + SIRET */}
                    <div className="readonly-grid two-cols" style={{ marginBottom: '1rem' }}>
                      <div className="readonly-field">
                        <label>Nom de l'organisme</label>
                        <p>{acheteur.nom || '-'}</p>
                      </div>
                      <div className="readonly-field">
                        <label>SIRET</label>
                        <p>{acheteur.siret ? acheteur.siret.replace(/(.{3})(.{3})(.{3})(.{5})/, '$1 $2 $3 $4') : '-'}</p>
                      </div>
                    </div>
                    {/* Adresse + Type d'organisme sur la même ligne */}
                    <div className="readonly-grid two-cols" style={{ marginBottom: '1rem' }}>
                      <div className="readonly-field">
                        <label>Adresse</label>
                        <p>{
                          [acheteur.adresse, acheteur.code_postal && acheteur.ville ? `${acheteur.code_postal} ${acheteur.ville}` : (acheteur.code_postal || acheteur.ville), acheteur.pays]
                            .filter(Boolean).join(', ') || '-'
                        }</p>
                      </div>
                      <div className="readonly-field">
                        <label>Type d'organisme</label>
                        <p>{acheteur.type_organisme || '-'}</p>
                      </div>
                    </div>
                    {/* Présentation texte */}
                    <div className="readonly-grid" style={{ gridTemplateColumns: '1fr', marginBottom: '0.5rem' }}>
                      <div className="readonly-field">
                        <label>Présentation de l'organisme acheteur</label>
                        {acheteur.presentation ? (
                          <p className="readonly-text-block">{acheteur.presentation}</p>
                        ) : (
                          <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '1.3rem' }}>
                            Aucune présentation renseignée.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </SectionPliable>

                {/* Section Représentant légal — toujours affiché (3 représentants) */}
                <SectionPliable title="Représentant légal" className="readonly-section-pliable">
                  <div className="readonly-section-content">
                    {[
                      { prenom: acheteur.representant_1_prenom, nom: acheteur.representant_1_nom, fonction: acheteur.representant_1_fonction, label: 'N°1' },
                      { prenom: acheteur.representant_2_prenom, nom: acheteur.representant_2_nom, fonction: acheteur.representant_2_fonction, label: 'N°2' },
                      { prenom: acheteur.representant_3_prenom, nom: acheteur.representant_3_nom, fonction: acheteur.representant_3_fonction, label: 'N°3' },
                    ].map((rep, i) => {
                      const isEmpty = !rep.prenom && !rep.nom && !rep.fonction;
                      return (
                        <div key={i} style={i > 0 ? { marginTop: '1.25rem' } : {}}>
                          <p style={{ fontWeight: 700, fontSize: '1.3rem', color: '#595959', marginBottom: '0.5rem' }}>
                            Représentant {rep.label}
                          </p>
                          {isEmpty ? (
                            <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Non renseigné</p>
                          ) : (
                            <div className="readonly-grid three-cols">
                              <div className="readonly-field">
                                <label>Prénom</label>
                                <p>{rep.prenom || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Non renseigné</span>}</p>
                              </div>
                              <div className="readonly-field">
                                <label>Nom</label>
                                <p>{rep.nom || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Non renseigné</span>}</p>
                              </div>
                              <div className="readonly-field">
                                <label>Fonction</label>
                                <p>{rep.fonction || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Non renseigné</span>}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </SectionPliable>

                {/* Section Périmètre géographique d'achat — carte de France en lecture seule */}
                <SectionPliable title="Périmètre géographique d'achat" className="readonly-section-pliable">
                  <div className="readonly-section-content">
                    {acheteur.perimetre_detail
                      && (acheteur.perimetre_detail.type === 'national'
                        || acheteur.perimetre_detail.regions.length > 0
                        || acheteur.perimetre_detail.departements.length > 0) ? (
                      <PerimetreMapDisplay value={acheteur.perimetre_detail} />
                    ) : (
                      <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Périmètre non renseigné</p>
                    )}
                  </div>
                </SectionPliable>

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcheteurModal;
export { DEPT_NAMES, COUNTRY_FLAGS, formatDepartement };
